// oncare-frontend/src/components/NurseAssignments/NurseListView/NurseListContainer.jsx
import React, { useEffect, useState, useCallback, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import PatientTableComponent from "../PatientTable/PatientTableComponent"
import { CiExport } from "react-icons/ci"
import { IoSearchOutline } from "react-icons/io5"
import {
	confirmedCancellationNoShow,
	confirmedCompletedAllCases,
	getTimeForTable,
	endTimeAllCases
} from "../../../utils/realTimeUtils"
import PatientFilter from "../PatientTable/PatientFilter"
import ColumnSelector from "../PatientTable/ColumnSelector"
import {
	handleExport,
	filterAndSortData as sharedFilterAndSortData,
	handleColumnsChange as sharedHandleColumnsChange,
	loadSavedColumns,
	handleSearch as sharedHandleSearch,
	saveFilters,
	loadSavedFilters,
	saveSortConfig,
	loadSavedSortConfig
} from "../utils/SharedListUtils"
import useLogging from "../../../hooks/useLogging"
import {
	setListViewColumns,
	setListViewFilters,
	setListViewSortConfig
} from "../../../features/users/userSlice"

const NurseListContainer = ({
	nurseId,
	acuityValue,
	setNotification,
	context = "default",
	isToday = true
}) => {
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)

	const logging = useLogging()
	const resetPaginationRef = useRef(null)
	const { getAccessTokenSilently } = useAuth0()
	const dispatch = useDispatch()

	// Get user state from Redux
	const userState = useSelector(state => state.user)

	// Create context-specific keys and paths
	const contextKey = context === "default" ? "DEFAULT" : ``
	const reduxPath = context === "default" ? "DEFAULT" : `nurseListView`

	// Get nurse list view preferences based on context
	const reduxColumns = userState[reduxPath]?.columns || null
	const reduxFilters = userState[reduxPath]?.filters || null
	const reduxSortConfig = userState[reduxPath]?.sortConfig || null

	const patientSchedule = useSelector(state =>
		sandbox
			? state.sandbox.patientSchedule
			: isTomorrowMode
				? state.tomorrow.patientSchedule
				: state.schedule.patientSchedule
	)
	const nurseSchedule = useSelector(state =>
		sandbox
			? state.sandbox.nurseSchedule
			: isTomorrowMode
				? state.tomorrow.nurseSchedule
				: state.schedule.nurseSchedule
	)

	const chairSchedule = useSelector(state =>
		sandbox
			? state.sandbox.chairSchedule
			: isTomorrowMode
				? state.tomorrow.chairSchedule
				: state.schedule.chairSchedule
	)

	const [filteredData, setFilteredData] = useState(patientSchedule)
	const [query, setQuery] = useState("")
	const [activeFilters, setActiveFilters] = useState([])
	const [filterRelationship, setFilterRelationship] = useState("AND")
	const [filters, setFilters] = useState({
		acuity: [],
		chair: [],
		startTime: "",
		endTime: "",
		lengthRange: [0, 12], // initial range: 0 to 12 hours
		progressRange: "",
		nurse: [],
		infusionType: [],
		reasonForVisit: [],
		referringDepartment: [],
		patientStatus: []
	})
	const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" })
	const [visibleColumns, setVisibleColumns] = useState(null)
	const COLUMNS_STORAGE_KEY = `nurseListViewColumns${contextKey}`
	const FILTERS_STORAGE_KEY = `nurseListViewFilters${contextKey}`
	const SORT_STORAGE_KEY = `nurseListViewSortConfig${contextKey}`

	// Define columns with their data types for the filter component
	const availableColumns = [
		{
			id: "nurse",
			name: "Nurse Name",
			field: "assignedNurse",
			dataType: "list",
			default: true
		},
		{ id: "chair", name: "Chair", field: "assignedChair", dataType: "list", default: true },
		{
			id: "patient",
			name: "Patient Name",
			field: "patientName",
			dataType: "string",
			default: true
		},
		{
			id: "startTime",
			name: "Start Time",
			field: "actualStartTime",
			dataType: "time",
			default: true
		},
		{
			id: "endTime",
			name: "End Time",
			field: "actualEndTime",
			dataType: "time",
			default: true
		},
		{
			id: "length",
			name: "Infusion Length",
			field: "length",
			dataType: "number",
			default: true
		},
		{
			id: "infusionType",
			name: "Infusion Type",
			field: "infusionType",
			dataType: "list",
			default: true
		},
		{
			id: "reasonForVisit",
			name: "Reason for Visit",
			field: "reasonForVisit",
			dataType: "string",
			default: true
		},
		{
			id: "referringDepartment",
			name: "Referring Department",
			field: "department",
			dataType: "list",
			default: true
		},
		{ id: "acuity", name: "Acuity", field: "acuity", dataType: "list", default: true },
		{
			id: "clinicAppointment",
			name: "Clinic Appointment Start Time",
			field: "clinicAppointmentTime",
			dataType: "time",
			default: false
		},
		{
			id: "arrivalTime",
			name: "Arrival Time",
			field: "arrivalTime",
			dataType: "time",
			default: false
		},
		{
			id: "bookedDate",
			name: "Booked Date",
			field: "bookedDate",
			dataType: "string",
			default: false
		},
		{
			id: "visitStatus",
			name: "Visit Status",
			field: "internalStatus",
			dataType: "list",
			default: false
		},
		{
			id: "clinicProvider",
			name: "Clinic Provider",
			field: "clinicProvider",
			dataType: "string",
			default: false
		},
		{
			id: "linkedClinicAppointment",
			name: "Clinic Appointment Type",
			field: "linkedClinicAppointment",
			dataType: "list",
			default: false
		},
		{ id: "notes", name: "Notes", field: "notes", dataType: "string", default: false }
	]

	useEffect(() => {
		if (acuityValue !== null && acuityValue !== undefined) {
			setFilters(prev => ({ ...prev, acuity: acuityValue }))
		}
	}, [acuityValue])

	const filterData = useCallback(
		(queryString = "") => {
			let filtered = sharedFilterAndSortData(
				patientSchedule,
				queryString,
				filters,
				nurseSchedule,
				sortConfig,
				{
					nurseId,
					sandbox,
					isTomorrowMode,
					confirmedCancellationNoShow,
					confirmedCompletedAllCases
				}
			)

			// Apply advanced filters if they exist
			if (activeFilters.length > 0) {
				filtered = applyAdvancedFilters(filtered, activeFilters, filterRelationship)
			}

			setFilteredData(filtered)
		},
		// `applyAdvancedFilters` is stable within this component context.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			patientSchedule,
			filters,
			activeFilters,
			nurseId,
			nurseSchedule,
			sortConfig,
			sandbox,
			isTomorrowMode,
			filterRelationship
		]
	)

	// Apply the advanced filters from PatientFilter component
	const applyAdvancedFilters = (data, filters, relationship = "AND") => {
		if (!filters.length) return data

		return data.filter(item => {
			if (relationship === "AND") {
				// All conditions must match (AND)
				return filters.every(filter => evaluateFilter(item, filter))
			} else {
				// Any condition can match (OR)
				return filters.some(filter => evaluateFilter(item, filter))
			}
		})
	}

	// Evaluate a single filter against an item
	const evaluateFilter = (item, filter) => {
		// Handle calculation filter type (time difference)
		if (filter.type === "calculation") {
			let time1, time2

			// Use appropriate time values based on column ID
			if (filter.field1 === "startTime") {
				time1 = getTimeForTable(item)
			} else if (filter.field1 === "endTime") {
				time1 = endTimeAllCases(item, sandbox, isTomorrowMode)
			} else {
				// For other time fields, use the field value directly
				const column = availableColumns.find(c => c.id === filter.field1)
				time1 = column ? parseInt(item[column.field] || 0) : 0
			}

			if (filter.field2 === "startTime") {
				time2 = getTimeForTable(item)
			} else if (filter.field2 === "endTime") {
				time2 = endTimeAllCases(item, sandbox, isTomorrowMode)
			} else {
				// For other time fields, use the field value directly
				const column = availableColumns.find(c => c.id === filter.field2)
				time2 = column ? parseInt(item[column.field] || 0) : 0
			}

			const difference = Math.abs(time1 - time2)
			const compareValue = parseInt(filter.value || 0)

			switch (filter.operator) {
				case "greater_than":
					return !isNaN(difference) && !isNaN(compareValue) && difference > compareValue
				case "less_than":
					return !isNaN(difference) && !isNaN(compareValue) && difference < compareValue
				case "equals":
					return !isNaN(difference) && !isNaN(compareValue) && difference === compareValue
				default:
					return true
			}
		}

		// Handle standard filters
		const column = availableColumns.find(c => c.id === filter.property)
		if (!column) return true

		const fieldValue = item[column.field]

		switch (filter.operator) {
			case "equals":
				if (column.dataType === "boolean") {
					return String(fieldValue) === filter.value
				}
				return fieldValue === filter.value // Using loose equality for type conversion

			case "contains":
				return (
					fieldValue &&
					String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase())
				)

			case "starts_with":
				return (
					fieldValue &&
					String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase())
				)

			case "ends_with":
				return (
					fieldValue &&
					String(fieldValue).toLowerCase().endsWith(String(filter.value).toLowerCase())
				)

			case "is_empty":
				return fieldValue === undefined || fieldValue === null || fieldValue === ""

			case "is_not_empty":
				return fieldValue !== undefined && fieldValue !== null && fieldValue !== ""

			case "greater_than":
				return parseInt(fieldValue) > parseInt(filter.value)

			case "less_than":
				return parseInt(fieldValue) < parseInt(filter.value)

			case "in_list":
				// Handle visit status filter specially by checking internalStatus field
				if (filter.property === "visitStatus") {
					return (
						filter.values &&
						filter.values.length > 0 &&
						filter.values.includes(item.internalStatus)
					)
				}
				// Handle acuity specially as a number comparison
				else if (filter.property === "acuity") {
					return (
						filter.values &&
						filter.values.length > 0 &&
						filter.values.some(v => Number(fieldValue) === Number(v))
					)
				}
				return (
					filter.values && filter.values.length > 0 && filter.values.includes(fieldValue)
				)

			case "not_in_list":
				// Handle visit status filter specially by checking internalStatus field
				if (filter.property === "visitStatus") {
					return (
						!filter.values ||
						filter.values.length === 0 ||
						!filter.values.includes(item.internalStatus)
					)
				}
				// Handle acuity specially as a number comparison
				else if (filter.property === "acuity") {
					return (
						!filter.values ||
						filter.values.length === 0 ||
						!filter.values.some(v => Number(fieldValue) === Number(v))
					)
				}
				return (
					!filter.values ||
					filter.values.length === 0 ||
					!filter.values.includes(fieldValue)
				)

			default:
				return true
		}
	}

	useEffect(() => {
		filterData(query)
	}, [filterData, query, filters])

	const handleSearch = e => {
		sharedHandleSearch(e, setQuery, filterData)
		// Reset pagination when search changes
		if (resetPaginationRef.current) {
			resetPaginationRef.current()
		}
	}

	const handleExportClick = () => {
		handleExport(filteredData, nurseSchedule, chairSchedule)
	}

	const handleColumnsChange = columns => {
		// Only save columns for non-default contexts
		if (context !== "default") {
			const actionCreator = data => setListViewColumns({ path: reduxPath, data })

			sharedHandleColumnsChange(
				columns,
				setVisibleColumns,
				COLUMNS_STORAGE_KEY,
				getAccessTokenSilently,
				logging,
				userState,
				dispatch,
				actionCreator
			)
		} else {
			// For default context, just update local state without saving
			setVisibleColumns(columns)
		}
	}

	// Handler for the advanced filters
	const handleApplyFilters = (filters, relationship = "AND") => {
		setActiveFilters(filters)
		setFilterRelationship(relationship)
		filterData(query) // Reapply filtering with the new filters

		// Only save filters for non-default contexts
		if (context !== "default") {
			const actionCreator = data => setListViewFilters({ path: reduxPath, data })

			// Save filters to localStorage and Auth0
			saveFilters(
				{ ...filters },
				filters,
				relationship,
				FILTERS_STORAGE_KEY,
				getAccessTokenSilently,
				logging,
				userState,
				dispatch,
				actionCreator
			)
		}

		// Reset pagination when advanced filters change
		if (resetPaginationRef.current) {
			resetPaginationRef.current()
		}
	}

	// Handler for sort configuration changes
	const handleSortConfigChange = (key, direction) => {
		const newConfig = { key, direction }
		setSortConfig(newConfig)

		// Only save sort config for non-default contexts
		if (context !== "default") {
			const actionCreator = data => setListViewSortConfig({ path: reduxPath, data })

			saveSortConfig(
				newConfig,
				SORT_STORAGE_KEY,
				getAccessTokenSilently,
				logging,
				userState,
				dispatch,
				actionCreator
			)
		}
	}

	// Load saved preferences on component mount
	useEffect(() => {
		// Only load saved preferences for non-default contexts
		if (context !== "default") {
			// Load saved columns from Redux first
			loadSavedColumns(
				setVisibleColumns,
				COLUMNS_STORAGE_KEY,
				getAccessTokenSilently,
				logging,
				reduxColumns
			)

			// Load saved filters from Redux first
			loadSavedFilters(
				setFilters,
				setActiveFilters,
				setFilterRelationship,
				FILTERS_STORAGE_KEY,
				getAccessTokenSilently,
				logging,
				reduxFilters
			)

			// Load saved sort configuration from Redux first
			loadSavedSortConfig(
				setSortConfig,
				SORT_STORAGE_KEY,
				getAccessTokenSilently,
				logging,
				reduxSortConfig
			)
		}
		// `getAccessTokenSilently` is stable across renders per @auth0/auth0-react; `logging` and setters are intentionally omitted.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [context, reduxColumns, reduxFilters, reduxSortConfig])

	if (nurseId === null && filteredData.length === 0) {
		return null
	}

	return (
		<div className="flex flex-col h-full">
			{nurseId !== null && (
				<div className="flex justify-between items-center py-4 bg-white sticky top-0 z-20">
					<div className="relative w-full max-w-xs flex items-center">
						<IoSearchOutline className="absolute left-3 text-gray-400" />
						<input
							type="text"
							className="
							sensitive ph-no-capture
							py-5 px-4 w-screen border border-transparent rounded-md pl-8 
                            text-sm h-8 bg-gray-100 text-gray-600 placeholder-gray-500 
                            outline-none focus:outline-none focus:ring-0 focus:border-customBlue"
							placeholder="Search by Patient Name"
							value={query}
							onChange={handleSearch}
						/>
					</div>
					<div className="flex items-center space-x-2">
						<ColumnSelector
							allColumns={availableColumns}
							activeColumns={
								visibleColumns ||
								availableColumns.filter(col => col.default).map(col => col.id)
							}
							onColumnsChange={handleColumnsChange}
						/>

						<PatientFilter
							columns={availableColumns}
							applyFilters={handleApplyFilters}
							nurseSchedule={nurseSchedule}
							chairSchedule={chairSchedule}
							initialFilters={activeFilters}
							initialFilterRelationship={filterRelationship}
							patientSchedule={patientSchedule}
						/>

						<button
							className="py-2 px-4 bg-white border border-gray-300 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-100 focus:outline-none"
							onClick={handleExportClick}
						>
							<div className="flex items-center gap-1">
								<span className="inline-flex">
									<CiExport className="h-4 w-4 text-gray-600" />
								</span>
								<span className="font-medium">Export</span>
							</div>
						</button>
					</div>
				</div>
			)}

			<div className="overflow-x-auto">
				<PatientTableComponent
					filteredData={filteredData}
					nurseSchedule={nurseSchedule}
					chairSchedule={chairSchedule}
					setNotification={setNotification}
					query={query}
					sortConfig={sortConfig}
					setSortConfig={handleSortConfigChange}
					visibleColumns={visibleColumns}
					onColumnsChange={handleColumnsChange}
					resetPaginationRef={resetPaginationRef}
					availableColumnsFromParent={availableColumns}
					isToday={isToday}
				/>
			</div>
		</div>
	)
}

export default NurseListContainer
